import { useEffect, useState } from "react";
import apiClient from "../../services/apiClient";
import toast from "../../utils/toast";
import { getCookie } from "../../utils/cookies";

const PAGE_SIZE = 10;

function Pagination({ currentPage, totalItems, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  if (totalPages <= 1) return null;

  return (
    <div className="pagination-row">
      <button
        className="btn-sm"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Prev
      </button>
      <span className="pagination-info">
        Page {currentPage} of {totalPages}
      </span>
      <button
        className="btn-sm"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );
}

function StatusBadge({ status }) {
  const badgeClass = status === "Closed" ? "green" : "gold";
  return <span className={`badge ${badgeClass}`}>{status || "-"}</span>;
}

export default function ComplainList() {
  const [isComplainData, setComplainData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState(0);
  const [ticketIdFilter, setTicketIdFilter] = useState("");
  const [page, setPage] = useState(1);

  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [conversationData, setConversationData] = useState(null);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  useEffect(() => {
    fetchComplainList();
  }, []);

  const fetchComplainList = async (overrides = {}) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const statusId = overrides.statusId ?? statusFilter;
      const ticketId = overrides.ticketId ?? (Number(ticketIdFilter) || 0);

      const res = await apiClient.post("/App/GetTicketList", {
        appSession: {
          appid: "ROUNDPAYAPPID13APR20191351",
          imei: "d593bdd26dfbea56",
          loginTypeID: 1,
          regKey: "",
          serialNo: "d593bdd26dfbea56",
          session: getCookie("session") || "",
          sessionID: getCookie("sessionID") || "",
          uid: 0,
          userID: userData.userID || "",
          version: "1.0",
        },
        request: {
          StatusId: statusId,
          TicketId: ticketId,
        },
      });

      if (res.statuscode == 1) {
        setComplainData(res.data);
      } else {
        setComplainData(null);
        toast.error(res.msg || "No data found");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setIsLoading(false);
    }
  };

  function handleSearch() {
    setPage(1);
    fetchComplainList();
  }

  function handleReset() {
    setStatusFilter(0);
    setTicketIdFilter("");
    setPage(1);
    fetchComplainList({ statusId: 0, ticketId: 0 });
  }

  const statusList = isComplainData?.ticketStatusList || [];
  const ticketRows = isComplainData?.ticketInfoList || [];
  const ticketPaginated = ticketRows.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const fetchConversationList = async (ticketId) => {
    setIsConversationLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/App/GetTicketConversationList", {
        appSession: {
          appid: "ROUNDPAYAPPID13APR20191351",
          imei: "d593bdd26dfbea56",
          loginTypeID: 1,
          regKey: "",
          serialNo: "d593bdd26dfbea56",
          session: getCookie("session") || "",
          sessionID: getCookie("sessionID") || "",
          uid: 0,
          userID: userData.userID || "",
          version: "1.0",
        },
        request: {
          TicketId: ticketId,
        },
      });

      if (res.statuscode == 1) {
        setConversationData(res.data);
      } else {
        setConversationData(null);
        toast.error(res.msg || "No conversation found");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setIsConversationLoading(false);
    }
  };

  function openConversationModal(ticket) {
    setActiveTicket(ticket);
    setConversationData(null);
    setReplyMessage("");
    setReplyAttachments([]);
    setIsConversationModalOpen(true);
    fetchConversationList(ticket.ticketId);
  }

  function closeConversationModal() {
    setIsConversationModalOpen(false);
    setActiveTicket(null);
    setConversationData(null);
    setReplyMessage("");
    setReplyAttachments([]);
  }

  const submitReply = async (isTicketStatusClosed) => {
    if (!replyMessage.trim()) {
      toast.error("Please type a message");
      return;
    }

    setIsSubmittingReply(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const ticketRequest = {
        UserID: userData.userID || "",
        SessionID: getCookie("sessionID") || "",
        Session: getCookie("session") || "",
        LoginTypeID: 1,
        APPID: "ROUNDPAYAPPID13APR20191351",
        IMEI: "d593bdd26dfbea56",
        SerialNo: "d593bdd26dfbea56",
        Version: "1.0",
        RegKey: "",
        DepartmentId: activeTicket?.departmentId,
        TicketId: activeTicket?.ticketId,
        Subject: activeTicket?.subject,
        MessageBody: `<p>${replyMessage}</p>`,
        IsTicketStatusClosed: isTicketStatusClosed,
      };

      const formData = new FormData();
      formData.append("TicketRequest", JSON.stringify(ticketRequest));
      replyAttachments.forEach((file) => {
        formData.append("files", file, file.name);
      });

      const res = await apiClient.post(
        "/App/CreateTicketOrSendTicketResponse",
        formData,
        { headers: { "Content-Type": undefined } },
      );

      if (res.statuscode == 1) {
        toast.success(res.msg);
        fetchComplainList();
        if (isTicketStatusClosed) {
          closeConversationModal();
        } else {
          setReplyMessage("");
          setReplyAttachments([]);
          fetchConversationList(activeTicket?.ticketId);
        }
      } else {
        toast.error(res.msg);
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to submit reply. Please try again.");
    } finally {
      setIsSubmittingReply(false);
    }
  };

  function handleSubmitReply() {
    submitReply(false);
  }

  function handleSubmitAndCloseReply() {
    submitReply(true);
  }

  const conversationMessages = Array.isArray(conversationData)
    ? conversationData
    : conversationData?.conversationList || [];

  const loggedInUserId = JSON.parse(getCookie("userData") || "{}").userID;

  return (
    <>
      <div className="content">
        <section className="page active" id="page-complain-list">
          <h3 className="section-title">Complaint List</h3>

          <div className="filters-row">
            <div className="field">
              <label>Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(Number(e.target.value))}
              >
                <option value={0}>All</option>
                {statusList.map((status) => (
                  <option key={status.statusId} value={status.statusId}>
                    {status.statusName}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Ticket ID</label>
              <input
                type="text"
                placeholder="Enter Ticket ID"
                value={ticketIdFilter}
                onChange={(e) => setTicketIdFilter(e.target.value)}
              />
            </div>
            <div className="field field-54854">
              <button className="btn-sm primary" onClick={handleSearch}>
                Search
              </button>
            </div>
            <div className="field field-54854">
              <button className="btn-sm" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>

          <div className="card">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Ticket ID</th>
                    <th>Subject</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Entry Date</th>
                    <th>Last Reply Date</th>
                    <th>Chat</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={8} className="table-empty">
                        Loading tickets...
                      </td>
                    </tr>
                  ) : ticketRows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="table-empty">
                        No tickets found.
                      </td>
                    </tr>
                  ) : (
                    ticketPaginated.map((ticket, idx) => (
                      <tr key={ticket.ticketId}>
                        <td className="mono">
                          {(page - 1) * PAGE_SIZE + idx + 1}
                        </td>
                        <td className="mono">{ticket.ticketId}</td>
                        <td className="strong">{ticket.subject}</td>
                        <td>{ticket.departmentName}</td>
                        <td>
                          <StatusBadge status={ticket.statusName} />
                        </td>
                        <td>{ticket.entryDate || "-"}</td>
                        <td>{ticket.lastReplyDate || "-"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-sm primary"
                            onClick={() => openConversationModal(ticket)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={page}
              totalItems={ticketRows.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </section>
      </div>

      {isConversationModalOpen && (
        <div className="modal-overlay active">
          <div
            className="modal wide"
            style={{ maxHeight: "88vh", overflowY: "auto" }}
          >
            <span className="modal-close" onClick={closeConversationModal}>
              &times;
            </span>

            <div className="modal-head">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M21 11.5a8.4 8.4 0 01-8.9 8.4 8.7 8.7 0 01-4-1L3 20l1.1-4.3A8.4 8.4 0 1121 11.5z" />
              </svg>
              <h3>
                Ticket #{activeTicket?.ticketId} — {activeTicket?.subject}
              </h3>
            </div>

            <div
              style={{
                maxHeight: 260,
                overflowY: "auto",
                marginBottom: 20,
                padding: "4px 2px",
              }}
            >
              {isConversationLoading ? (
                <p style={{ color: "var(--text-faint)", fontSize: 13.5 }}>
                  Loading conversation...
                </p>
              ) : conversationMessages.length === 0 ? (
                <p style={{ color: "var(--text-faint)", fontSize: 13.5 }}>
                  No previous conversation.
                </p>
              ) : (
                conversationMessages.map((msg, idx) => {
                  const isOwnMessage =
                    String(msg.userId) === String(loggedInUserId);
                  return (
                    <div
                      key={msg.ticketResponseId ?? idx}
                      style={{
                        display: "flex",
                        justifyContent: isOwnMessage
                          ? "flex-end"
                          : "flex-start",
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          maxWidth: "75%",
                          padding: "10px 14px",
                          background: isOwnMessage
                            ? "var(--gold)"
                            : "var(--bg-elev-2)",
                          color: isOwnMessage ? "#1a1a1a" : "var(--text)",
                          borderRadius: 9,
                          fontSize: 13,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            marginBottom: 4,
                            color: isOwnMessage ? "#5a3d00" : "var(--gold)",
                          }}
                        >
                          {msg.userName}
                        </div>
                        <div
                          dangerouslySetInnerHTML={{ __html: msg.messageBody }}
                        />
                        <div
                          style={{
                            fontSize: 11,
                            marginTop: 6,
                            textAlign: "right",
                            opacity: 0.7,
                          }}
                        >
                          {msg.entryDate}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="field">
              <label>Message</label>
              <textarea
                placeholder="Type your reply..."
                style={{ minHeight: 150 }}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
              />
            </div>

            <div className="field">
              <label>Attachments</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                multiple
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);
                  setReplyAttachments((prev) => [...prev, ...newFiles]);
                  e.target.value = "";
                }}
              />
              <div
                className="field-note"
                style={{ color: "var(--text-faint)" }}
              >
                Allowed File Extensions: .jpg, .jpeg, .png, .pdf
              </div>
              {replyAttachments.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  {replyAttachments.map((file, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: "var(--bg-elev-2)",
                        border: "1px solid var(--border)",
                        borderRadius: 7,
                        padding: "6px 10px",
                        fontSize: 12.5,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name}
                      </span>
                      <span
                        onClick={() =>
                          setReplyAttachments((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                        style={{
                          cursor: "pointer",
                          color: "var(--text-faint)",
                          marginLeft: 10,
                        }}
                      >
                        &times;
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
            >
              <button
                type="button"
                className="btn-sm"
                onClick={handleSubmitAndCloseReply}
                disabled={isSubmittingReply}
              >
                Submit &amp; Close
              </button>
              <button
                type="button"
                className="btn-primary btn-primary-top-2"
                style={{ width: "auto", padding: "10px 26px" }}
                onClick={handleSubmitReply}
                disabled={isSubmittingReply}
              >
                {isSubmittingReply ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
